import { Injectable } from '@angular/core';
import { Axis, UIElement } from '@lightningchart/lcjs';
import { LABEL_OVERLAP_THRESHOLD } from './lightning-chart-ui-constants';

export interface LightningChartContainLabels {
    uiSeriesLabels: UIElement[],
}

/**
 *
 * Create Lightning Charts events
 *
 * Angular Singleton
 * IMPORTANT: IT SHOULD NOT CONTAIN ANY ATTRIBUTES, everything should be "static"
 *
 */
@Injectable({
    providedIn: 'root'
  })
export class LightningChartSEventsFactory {

    public installxAxisOnIntervalChange(
        xAxis: Axis,
        yAxis: Axis,
        labelsContainer: LightningChartContainLabels,
        callbacks?: {
            check?: (axis: Axis, start: number, end: number) => boolean,
            callback?: () => void
        }
    ) {
        const self = this;

        xAxis.onIntervalChange((axis, start, end) => {
            if(callbacks?.check) {
                if(!callbacks.check(axis, start, end)) return;
            }

            self.updateChartLabels(labelsContainer, start, end, yAxis);

            if(callbacks?.callback) {
                callbacks?.callback();
            }
        });
    }

    private updateChartLabels(labelsContainer: LightningChartContainLabels, start: number, end: number, yAxis: Axis) {
        if (start < 0) return;

        const xRange = end - start;
        const labelSpacing = xRange * LABEL_OVERLAP_THRESHOLD;
        const yRange = Math.abs(yAxis.getInterval().end - yAxis.getInterval().start);
        const yThresholdPercentage = LABEL_OVERLAP_THRESHOLD;
        const yThreshold = yThresholdPercentage * yRange;

        // Dictionary to keep track of label positions
        const labelPositions = new Map<number, { x: number, y: number }>();

        const adjustLabelPosition = (label: UIElement, labelIndex: number) => {
            let newX = end;
            const currentY = label.getPosition().y;

            const isOverlapping = (x: number, y: number, currentIndex: number) => {
                for (const [index, pos] of labelPositions.entries()) {
                    if (index === currentIndex) continue; // Skip self comparison

                    const { x: posX, y: posY } = pos;
                    const xDifference = Math.abs(x - posX);
                    const yDifference = Math.abs(y - posY);

                    if (xDifference < labelSpacing && yDifference < yThreshold) {
                        return true;
                    }
                }
                return false;
            };

            while (isOverlapping(newX, currentY, labelIndex)) {
                newX -= labelSpacing;
            }

            // Set the adjusted position
            label.setPosition({
                x: newX,
                y: currentY
            });

            // Update the dictionary with the new position
            labelPositions.set(labelIndex, { x: newX, y: currentY });
        };

        // Initial setting of positions and adding them to the dictionary
        for (let i = 0; i < labelsContainer.uiSeriesLabels.length; i++) {
            const label = labelsContainer.uiSeriesLabels[i];
            const initialX = end;
            const initialY = label.getPosition().y;

            // Add initial position to dictionary
            labelPositions.set(i, { x: initialX, y: initialY });

            // Adjust position only if there are more than one labels
            if (labelsContainer.uiSeriesLabels.length > 1) {
                adjustLabelPosition(label, i);
            } else {
                // Set the position directly to end if it's the only label
                label.setPosition({
                    x: end,
                    y: initialY
                });
            }
        }
    }

}
